import React from 'react'

// components
import { LoaderSpinner } from '@/components/ui/LoaderSpinner.component'

// styles
import { buttonStyle } from './styles'
import { cssClass, rem } from '@/shared/utils/styles'
import { vars } from '@/styles/v2/vars.css'
import { LabelButton } from './LabelButton.component'
import { composeRefs } from '@/shared/utils'
import { useRouter } from 'next/router'

interface SizeInterface {
  height?: number
  fontSize?: number
  padding?: number
  gap?: number
  borderRadius?: number
}

const sizes = {
  xSmall: {
    height: rem(35),
    fontSize: rem(14),
    padding: 16,
    gap: 6,
    borderRadius: 10,
  },
  xs: {
    height: rem(37),
    fontSize: rem(14),
    padding: 16,
    gap: 6,
    borderRadius: 10,
  },
  small: {
    height: rem(46),
    fontSize: rem(14),
    padding: 24,
    gap: 12,
    borderRadius: 12,
  },
  regular: {
    height: rem(48),
    fontSize: rem(16),
    padding: 24,
    gap: 6,
    borderRadius: 12,
  },
  large: {
    height: rem(56),
    fontSize: rem(16),
    padding: 32,
    gap: 12,
    borderRadius: 16,
  },
}

export interface ButtonProps
  extends Partial<React.ComponentPropsWithoutRef<'button'>> {
  label?: string
  children?: React.ReactNode
  variation?: keyof typeof buttonStyle
  disabled?: boolean
  loading?: boolean
  fullWidth?: boolean
  size?: keyof typeof sizes
  customSize?: SizeInterface
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  onlyIcon?: boolean
  to?: string
  onClick?: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

export const ButtonComponent = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variation,
      disabled,
      style,
      onClick,
      label,
      className = '',
      loading = false,
      onMouseEnter,
      fullWidth = false,
      onMouseLeave,
      iconLeft,
      iconRight,
      onlyIcon = false,
      size: originalSize = 'regular',
      to,
      customSize,
      ...rest
    },
    forwardedRef
  ) => {
    const ref = React.useRef<HTMLButtonElement>(null)
    const router = useRouter()
    const onClickButton = () => {
      if (ref.current) {
        setTimeout(() => ref?.current?.blur(), 300)
      }
      if (onClick) {
        onClick()
      }
      if (to) {
        router.push(to)
      }
    }
    const size = {
      ...sizes[originalSize],
      ...customSize,
    }
    const padding = onlyIcon ? size.padding / 2 : size.padding

    const buttonStyles = {
      ...style,
      ...size,
      padding: `0 ${padding}px`,
    }

    return (
      <button
        style={buttonStyles}
        className={cssClass([
          buttonStyle[variation || 'primary'],
          fullWidth ? 'full-w' : '',
          disabled && 'cursor-disabled',
          className,
        ])}
        onClick={onClickButton}
        disabled={disabled}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        {...rest}
        ref={composeRefs(ref, forwardedRef)}
      >
        {iconLeft && !loading && iconLeft}
        {label && !loading && <LabelButton label={label} />}
        {!loading && children}
        {iconRight && !loading && iconRight}
        {loading && (
          <LoaderSpinner
            background={
              variation === 'secondary'
                ? vars.colors.green.primary
                : 'transparent'
            }
          />
        )}
      </button>
    )
  }
)

ButtonComponent.displayName = 'ButtonComponent'
